import React, { useEffect, useState, useMemo } from 'react';
import PortalV2Layout from './PortalV2Layout';
import { useAuth } from '../contexts/AuthContext';
import { formatPhone } from './ClientV2Settings';
import {
  Search, Users, AlertCircle, Archive, ArchiveRestore, Edit2, Trash2, X,
  RefreshCw, Send, UserPlus, UserCheck, Clock, Mail, Phone, Building2,
  Link as LinkIcon, Plus, CheckCircle, XCircle,
} from 'lucide-react';

/**
 * Admin V2 Clients — V1 layout (AdminClientsEnhanced) reskinned in dark editorial.
 * Includes registered + potential clients, stat-card filters, status badges,
 * icon-only actions, email linking modal, edit modal, archive/delete.
 */
const AdminV2Clients = () => {
  const { authFetch } = useAuth();
  const [allClients, setAllClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [search, setSearch] = useState('');
  const [viewFilter, setViewFilter] = useState('all'); // all | registered | potential
  const [statusFilter, setStatusFilter] = useState('all');
  const [showArchived, setShowArchived] = useState(false);
  const [stats, setStats] = useState({ total: 0, registered: 0, potential: 0, pending: 0, invited: 0, notInvited: 0, archived: 0 });

  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ first_name: '', last_name: '', company_name: '', phone: '' });
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Email linking
  const [linkUser, setLinkUser] = useState(null);
  const [linkedEmails, setLinkedEmails] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailError, setEmailError] = useState('');

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);

  const load = async (includeArchived = false) => {
    setLoading(true);
    try {
      const [usersRes, potentialRes] = await Promise.all([
        authFetch(`/api/admin/clients?include_archived=${includeArchived}`),
        authFetch(`/api/admin/potential-clients?include_archived=${includeArchived}`),
      ]);
      if (!usersRes.ok || !potentialRes.ok) { setError('Failed to fetch client data'); return; }
      const usersData = await usersRes.json();
      const potentialData = await potentialRes.json();

      const registered = (usersData.users || []).map(u => ({ ...u, type: 'registered', job_count: 0 }));
      const potential = (potentialData.potential_clients || []).map(c => ({
        id: c.email, email: c.email, company_name: c.company_name, job_count: c.job_count,
        type: 'potential', status: 'not_registered', invited: c.invited || false,
        invited_at: c.invited_at, archived: c.archived || false,
      }));

      const archivedCount = (usersData.archived_count || 0) + (potentialData.archived_count || 0);

      // Batch job counts
      if (registered.length > 0) {
        try {
          const r = await authFetch('/api/admin/client-job-counts-batch', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(registered.map(c => c.email)),
          });
          if (r.ok) {
            const counts = (await r.json()).counts || {};
            registered.forEach(c => { c.job_count = counts[c.email?.toLowerCase()] || 0; });
          }
        } catch {}
      }

      // Dedupe potential against registered + linked emails
      const regSet = new Set(registered.map(c => c.email?.toLowerCase()));
      const linkedSet = new Set();
      registered.forEach(c => (c.linked_emails || []).forEach(e => linkedSet.add(e.toLowerCase())));
      const filteredPot = potential.filter(p => !regSet.has(p.email?.toLowerCase()) && !linkedSet.has(p.email?.toLowerCase()));

      const combined = [...registered, ...filteredPot].sort((a, b) => (b.job_count || 0) - (a.job_count || 0));
      setAllClients(combined);
      setStats({
        total: combined.length, registered: registered.length, potential: filteredPot.length,
        pending: usersData.pending_count || 0,
        invited: filteredPot.filter(p => p.invited).length,
        notInvited: filteredPot.filter(p => !p.invited).length,
        archived: archivedCount,
      });
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(showArchived); /* eslint-disable-next-line */ }, [showArchived]);

  const archive = async (client) => {
    setActionLoading(client.id || client.email);
    try {
      if (client.type === 'registered') {
        await authFetch(`/api/admin/user/${client.id}/archive`, { method: 'PATCH' });
      } else {
        await authFetch('/api/admin/archive-email', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: client.email, archived: !client.archived }),
        });
      }
      await load(showArchived);
    } finally { setActionLoading(null); }
  };

  const handleAction = async (userId, action) => {
    setActionLoading(userId);
    try {
      await authFetch('/api/admin/approve/user', {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, action }),
      });
      await load(showArchived);
    } finally { setActionLoading(null); }
  };

  const startEdit = (c) => {
    setEditing(c);
    setEditForm({ first_name: c.first_name || '', last_name: c.last_name || '', company_name: c.company_name || '', phone: formatPhone(c.phone || '') });
  };

  const saveEdit = async () => {
    setActionLoading(editing.id);
    try {
      const params = new URLSearchParams();
      if (editForm.first_name) params.append('first_name', editForm.first_name);
      if (editForm.last_name) params.append('last_name', editForm.last_name);
      if (editForm.company_name) params.append('company_name', editForm.company_name);
      if (editForm.phone !== undefined) params.append('phone', editForm.phone);
      await authFetch(`/api/admin/user/${editing.id}?${params.toString()}`, { method: 'PUT' });
      setEditing(null);
      await load(showArchived);
    } finally { setActionLoading(null); }
  };

  const deleteClient = async () => {
    setActionLoading(deleteConfirm.id);
    try {
      await authFetch(`/api/admin/user/${deleteConfirm.id}`, { method: 'DELETE' });
      setDeleteConfirm(null);
      await load(showArchived);
    } finally { setActionLoading(null); }
  };

  const openLinkModal = (u) => {
    setLinkUser(u); setLinkedEmails(u.linked_emails || []); setNewEmail(''); setEmailError('');
  };

  const addLinked = async (force = false) => {
    if (!newEmail.trim()) { setEmailError('Please enter an email'); return; }
    setEmailLoading(true); setEmailError('');
    try {
      const r = await authFetch(`/api/admin/user/${linkUser.id}/emails`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: newEmail.trim(), force }),
      });
      const d = await r.json();
      if (!r.ok) { setEmailError(d.detail || 'Failed to add email'); return; }
      if (d.needs_confirmation) {
        if (window.confirm(`Warning: ${d.message}\n\nGive ${linkUser.first_name} ${linkUser.last_name} access to view jobs for this email?`)) {
          await addLinked(true);
        }
        return;
      }
      if (d.success) { setLinkedEmails([...linkedEmails, newEmail.trim().toLowerCase()]); setNewEmail(''); await load(showArchived); }
    } catch (e) { setEmailError(e.message); }
    finally { setEmailLoading(false); }
  };

  const removeLinked = async (email) => {
    setEmailLoading(true);
    try {
      await authFetch(`/api/admin/user/${linkUser.id}/emails/${encodeURIComponent(email)}`, { method: 'DELETE' });
      setLinkedEmails(linkedEmails.filter(e => e.toLowerCase() !== email.toLowerCase()));
      await load(showArchived);
    } finally { setEmailLoading(false); }
  };

  // Invite URLs
  const inviteUrl = (email) => {
    const subject = 'WesTex Client Portal - Track Your Jobs Online';
    const body = `We are excited to introduce our new Client Portal, designed to give you real-time visibility into your process serving jobs.

Important: The portal is completely optional and is NOT a requirement for submitting jobs.

What you can do in the Client Portal:
• Track job status in real time
• View service attempts (with GPS when available)
• View and pay invoices online
• Download documents anytime
• See your complete job history

Get started: https://westexlegal.com/portal/register

Please register using this email address to ensure your existing jobs are linked.

Questions? Just reply to this email.`;
    return `https://mail.google.com/mail/?view=cm&to=${encodeURIComponent(email)}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };
  const massInviteUrl = () => {
    const list = allClients.filter(c => c.type === 'potential' && !c.invited).map(c => c.email);
    if (list.length === 0) return null;
    const subject = 'WesTex Client Portal - Track Your Jobs Online';
    const body = `We are excited to introduce our new Client Portal. Track your jobs in real time, pay invoices online, and see attempt history.\n\nGet started: https://westexlegal.com/portal/register\n\nUse your work email to link existing jobs.`;
    return `https://mail.google.com/mail/?view=cm&bcc=${encodeURIComponent(list.join(','))}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };
  const markInvited = async (email) => {
    try {
      const r = await authFetch(`/api/admin/mark-invited?email=${encodeURIComponent(email)}`, { method: 'POST' });
      if (r.ok) {
        setAllClients(prev => prev.map(c => c.email?.toLowerCase() === email.toLowerCase() ? { ...c, invited: true, invited_at: new Date().toISOString() } : c));
        setStats(s => ({ ...s, invited: s.invited + 1, notInvited: s.notInvited - 1 }));
      }
    } catch {}
  };

  const filtered = useMemo(() => {
    return allClients.filter(c => {
      if (viewFilter === 'registered' && c.type !== 'registered') return false;
      if (viewFilter === 'potential' && c.type !== 'potential') return false;
      if (statusFilter !== 'all' && c.type === 'registered' && c.status !== statusFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        return (c.email || '').toLowerCase().includes(q)
          || (c.first_name || '').toLowerCase().includes(q)
          || (c.last_name || '').toLowerCase().includes(q)
          || (c.company_name || '').toLowerCase().includes(q);
      }
      return true;
    });
  }, [allClients, viewFilter, statusFilter, search]);

  const StatusBadge = ({ c }) => {
    if (c.type === 'potential') {
      return c.invited
        ? <span className="pv2-pill" style={{ color: '#60a5fa' }}><Send size={9} strokeWidth={2} /> Invited</span>
        : <span className="pv2-pill" style={{ color: 'var(--pv2-text-muted)' }}><UserPlus size={9} strokeWidth={2} /> Not Invited</span>;
    }
    if (c.status === 'approved') return <span className="pv2-pill" style={{ color: 'var(--pv2-green-text)' }}><UserCheck size={9} strokeWidth={2} /> Registered</span>;
    if (c.status === 'pending') return <span className="pv2-pill" style={{ color: 'var(--pv2-amber-text)' }}><Clock size={9} strokeWidth={2} /> Pending</span>;
    if (c.status === 'rejected') return <span className="pv2-pill" style={{ color: 'var(--pv2-red-bright)' }}><XCircle size={9} strokeWidth={2} /> Rejected</span>;
    if (c.status === 'suspended') return <span className="pv2-pill" style={{ color: 'var(--pv2-text-muted)' }}><XCircle size={9} strokeWidth={2} /> Suspended</span>;
    return null;
  };

  const massInvite = massInviteUrl();

  // Bulk selection helpers — use email as universal key (works for both registered + potential)
  const toggleOne = (key) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  const toggleAll = () => {
    if (selectedIds.size === filtered.length && filtered.length > 0) setSelectedIds(new Set());
    else setSelectedIds(new Set(filtered.map(c => (c.email || c.id))));
  };
  const clearSelection = () => setSelectedIds(new Set());
  const archiveSelected = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Archive ${selectedIds.size} selected client${selectedIds.size > 1 ? 's' : ''}?`)) return;
    setBulkLoading(true);
    try {
      const userIds = [];
      const emails = [];
      filtered.forEach(c => {
        const key = c.email || c.id;
        if (!selectedIds.has(key)) return;
        if (c.type === 'registered' && c.id) userIds.push(c.id);
        else if (c.email) emails.push(c.email);
      });
      const tasks = [];
      if (userIds.length) {
        tasks.push(authFetch('/api/admin/bulk-archive-users', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_ids: userIds }),
        }));
      }
      if (emails.length) {
        tasks.push(authFetch('/api/admin/bulk-archive-emails', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ emails }),
        }));
      }
      await Promise.all(tasks);
      clearSelection();
      await load(showArchived);
    } finally { setBulkLoading(false); }
  };

  return (
    <PortalV2Layout
      eyebrow="Directory"
      title="Manage Clients"
      subtitle="Registered users and potential clients from ServeManager jobs."
      crumbs={<><span style={{ opacity: 0.5 }}>Admin /</span> <strong style={{ marginLeft: 6 }}>Clients</strong></>}
      action={
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {stats.notInvited > 0 && massInvite && (
            <a href={massInvite} target="_blank" rel="noopener noreferrer" className="pv2-btn pv2-btn-primary" data-testid="pv2-mass-invite">
              <Send size={14} strokeWidth={1.5} /> Mass Invite ({stats.notInvited})
            </a>
          )}
          <button onClick={() => load(showArchived)} disabled={loading} className="pv2-btn pv2-btn-ghost" data-testid="pv2-refresh-clients">
            <RefreshCw size={14} strokeWidth={1.5} className={loading ? 'pv2-skeleton' : ''} /> Refresh
          </button>
        </div>
      }
    >
      {error && <div className="pv2-alert pv2-alert-red"><AlertCircle size={14} /><span>{error}</span></div>}

      {/* Stat-card filters */}
      <section className="pv2-stats-grid" style={{ marginBottom: 24 }}>
        <button onClick={() => { setViewFilter('all'); setStatusFilter('all'); }}
          className="pv2-stat" style={{ textAlign: 'left', cursor: 'pointer', border: viewFilter === 'all' && statusFilter === 'all' ? '1px solid var(--pv2-red-bright)' : undefined, background: viewFilter === 'all' && statusFilter === 'all' ? 'rgba(248,113,113,0.05)' : undefined }} data-testid="pv2-stat-total">
          <div className="pv2-stat-label"><Users size={11} strokeWidth={1.5} style={{ marginRight: 6, verticalAlign: 'middle' }} />Total Clients</div>
          <div className="pv2-stat-value">{stats.total}</div>
        </button>
        <button onClick={() => { setViewFilter('registered'); setStatusFilter('all'); }}
          className="pv2-stat" style={{ textAlign: 'left', cursor: 'pointer', border: viewFilter === 'registered' ? '1px solid var(--pv2-green-text)' : undefined, background: viewFilter === 'registered' ? 'rgba(74,222,128,0.05)' : undefined }} data-testid="pv2-stat-registered">
          <div className="pv2-stat-label"><UserCheck size={11} strokeWidth={1.5} style={{ marginRight: 6, verticalAlign: 'middle' }} />Registered</div>
          <div className="pv2-stat-value" style={{ color: 'var(--pv2-green-text)' }}>{stats.registered}</div>
        </button>
        <button onClick={() => { setViewFilter('potential'); setStatusFilter('all'); }}
          className="pv2-stat" style={{ textAlign: 'left', cursor: 'pointer', border: viewFilter === 'potential' ? '1px solid #60a5fa' : undefined, background: viewFilter === 'potential' ? 'rgba(96,165,250,0.05)' : undefined }} data-testid="pv2-stat-potential">
          <div className="pv2-stat-label"><UserPlus size={11} strokeWidth={1.5} style={{ marginRight: 6, verticalAlign: 'middle' }} />Not Registered</div>
          <div className="pv2-stat-value" style={{ color: '#60a5fa' }}>{stats.potential}</div>
        </button>
        <button onClick={() => { setViewFilter('registered'); setStatusFilter('pending'); }}
          className="pv2-stat" style={{ textAlign: 'left', cursor: 'pointer', border: statusFilter === 'pending' ? '1px solid var(--pv2-amber-text)' : undefined, background: statusFilter === 'pending' ? 'rgba(251,191,36,0.05)' : undefined }} data-testid="pv2-stat-pending">
          <div className="pv2-stat-label"><Clock size={11} strokeWidth={1.5} style={{ marginRight: 6, verticalAlign: 'middle' }} />Pending Approval</div>
          <div className="pv2-stat-value" style={{ color: 'var(--pv2-amber-text)' }}>{stats.pending}</div>
        </button>
      </section>

      {/* Filters bar */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
        <div style={{ position: 'relative', flex: '1 1 280px' }}>
          <Search size={14} strokeWidth={1.5} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--pv2-text-subtle)' }} />
          <input className="pv2-input" placeholder="Search by name, email, or firm…" value={search} onChange={(e) => setSearch(e.target.value)} style={{ paddingLeft: 36 }} data-testid="pv2-admin-clients-search" />
        </div>
        {viewFilter === 'registered' && (
          <select className="pv2-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ flex: '0 0 180px' }}>
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="suspended">Suspended</option>
          </select>
        )}
        <button onClick={() => setShowArchived(s => !s)} className={`pv2-btn ${showArchived ? 'pv2-btn-primary' : 'pv2-btn-ghost'}`}>
          {showArchived ? <ArchiveRestore size={14} /> : <Archive size={14} />}
          {showArchived ? 'Hiding Archived' : `Show Archived (${stats.archived})`}
        </button>
      </div>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div data-testid="pv2-bulk-bar" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', background: 'var(--pv2-red-bright)', color: '#fff', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
          <strong style={{ fontSize: 14 }}>{selectedIds.size} client{selectedIds.size === 1 ? '' : 's'} selected</strong>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={clearSelection} style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)', padding: '8px 14px', cursor: 'pointer', fontSize: 12, fontFamily: 'JetBrains Mono, monospace', textTransform: 'uppercase', letterSpacing: 1 }} data-testid="pv2-bulk-clear">
              Clear Selection
            </button>
            <button onClick={archiveSelected} disabled={bulkLoading} style={{ background: '#fff', color: 'var(--pv2-red-bright)', border: 0, padding: '8px 14px', cursor: 'pointer', fontSize: 12, fontFamily: 'JetBrains Mono, monospace', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600 }} data-testid="pv2-bulk-archive">
              <Archive size={12} strokeWidth={2} style={{ verticalAlign: 'middle', marginRight: 6 }} />
              {bulkLoading ? 'Archiving…' : 'Archive Selected'}
            </button>
          </div>
        </div>
      )}

      <div className="pv2-table-wrap pv2-clients-table">
        <table className="pv2-table" data-testid="pv2-admin-clients-table">
          <thead>
            <tr>
              <th style={{ width: 36, paddingRight: 0 }}>
                <input type="checkbox" data-testid="pv2-bulk-select-all"
                  checked={filtered.length > 0 && selectedIds.size === filtered.length}
                  onChange={toggleAll}
                  style={{ accentColor: 'var(--pv2-red-bright)', cursor: 'pointer', width: 14, height: 14 }} />
              </th>
              <th>Client</th><th>Firm</th><th>Jobs</th><th>Status</th><th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="pv2-table-empty">Loading…</td></tr>}
            {!loading && filtered.length === 0 && <tr><td colSpan={6} className="pv2-table-empty"><Users size={20} style={{ opacity: 0.3, marginBottom: 8 }} />No clients match.</td></tr>}
            {!loading && filtered.map(c => {
              const key = c.email || c.id;
              return (
              <tr key={c.id || c.email} style={{ opacity: c.archived ? 0.6 : 1, background: selectedIds.has(key) ? 'rgba(248,113,113,0.05)' : undefined }}>
                <td style={{ paddingRight: 0 }}>
                  <input type="checkbox" data-testid={`pv2-bulk-select-${key}`}
                    checked={selectedIds.has(key)}
                    onChange={() => toggleOne(key)}
                    style={{ accentColor: 'var(--pv2-red-bright)', cursor: 'pointer', width: 14, height: 14 }} />
                </td>
                <td>
                  {c.type === 'registered' ? (
                    <div style={{ fontWeight: 500 }}>
                      {c.first_name} {c.last_name}
                      {c.archived && <span className="pv2-pill pv2-pill-gray" style={{ marginLeft: 8 }}><Archive size={9} /> Archived</span>}
                    </div>
                  ) : (
                    <div style={{ fontStyle: 'italic', color: 'var(--pv2-text-subtle)' }}>Not registered</div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--pv2-text-muted)', marginTop: 2 }}>
                    <Mail size={10} strokeWidth={1.5} /> {c.email}
                  </div>
                  {c.phone && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--pv2-text-muted)', marginTop: 2 }}>
                      <Phone size={10} strokeWidth={1.5} /> {c.phone}
                    </div>
                  )}
                  {c.linked_emails && c.linked_emails.length > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#60a5fa', marginTop: 2 }}>
                      <LinkIcon size={9} strokeWidth={1.5} /> {c.linked_emails.length} linked email{c.linked_emails.length !== 1 ? 's' : ''}
                    </div>
                  )}
                </td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                    <Building2 size={12} strokeWidth={1.5} color="var(--pv2-text-subtle)" /> {c.company_name || '—'}
                  </div>
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#60a5fa', fontFamily: 'JetBrains Mono, monospace' }}>
                    {c.job_count || 0}
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--pv2-text-subtle)', marginLeft: 4 }}>
                    {(c.job_count || 0) === 1 ? 'job' : 'jobs'}
                  </span>
                </td>
                <td style={{ whiteSpace: 'nowrap' }}><StatusBadge c={c} /></td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {c.type === 'potential' ? (
                    <>
                      <a href={inviteUrl(c.email)} target="_blank" rel="noopener noreferrer" onClick={() => markInvited(c.email)} title={c.invited ? 'Send another invite' : 'Send invite email'}
                        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, color: c.invited ? 'var(--pv2-text-muted)' : '#60a5fa' }}>
                        <Send size={14} strokeWidth={1.5} />
                      </a>
                      <button onClick={() => archive(c)} disabled={actionLoading === c.email} title={c.archived ? 'Unarchive' : 'Archive'}
                        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, background: 'transparent', border: 0, color: 'var(--pv2-amber-text)', cursor: 'pointer', marginLeft: 2 }}>
                        {c.archived ? <ArchiveRestore size={14} strokeWidth={1.5} /> : <Archive size={14} strokeWidth={1.5} />}
                      </button>
                    </>
                  ) : (
                    <>
                      {c.status === 'approved' && (
                        <button onClick={() => openLinkModal(c)} title="Link ServeManager emails"
                          style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, background: 'transparent', border: 0, color: '#60a5fa', cursor: 'pointer' }}>
                          <LinkIcon size={14} strokeWidth={1.5} />
                        </button>
                      )}
                      <button onClick={() => startEdit(c)} title="Edit client"
                        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, background: 'transparent', border: 0, color: 'var(--pv2-text-muted)', cursor: 'pointer', marginLeft: 2 }}>
                        <Edit2 size={14} strokeWidth={1.5} />
                      </button>
                      <button onClick={() => archive(c)} disabled={actionLoading === c.id} title={c.archived ? 'Unarchive' : 'Archive'}
                        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, background: 'transparent', border: 0, color: 'var(--pv2-amber-text)', cursor: 'pointer', marginLeft: 2 }}>
                        {c.archived ? <ArchiveRestore size={14} strokeWidth={1.5} /> : <Archive size={14} strokeWidth={1.5} />}
                      </button>
                      <button onClick={() => setDeleteConfirm(c)} title="Delete client"
                        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, background: 'transparent', border: 0, color: 'var(--pv2-red-bright)', cursor: 'pointer', marginLeft: 2 }}>
                        <Trash2 size={14} strokeWidth={1.5} />
                      </button>
                      {c.status === 'pending' && (
                        <>
                          <button onClick={() => handleAction(c.id, 'approve')} disabled={actionLoading === c.id} title="Approve"
                            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, background: 'transparent', border: 0, color: 'var(--pv2-green-text)', cursor: 'pointer', marginLeft: 2 }}>
                            <CheckCircle size={14} strokeWidth={1.5} />
                          </button>
                          <button onClick={() => handleAction(c.id, 'reject')} disabled={actionLoading === c.id} title="Reject"
                            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, background: 'transparent', border: 0, color: 'var(--pv2-red-bright)', cursor: 'pointer', marginLeft: 2 }}>
                            <XCircle size={14} strokeWidth={1.5} />
                          </button>
                        </>
                      )}
                      {c.status === 'approved' && (
                        <button onClick={() => handleAction(c.id, 'suspend')} disabled={actionLoading === c.id} title="Suspend"
                          style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, background: 'transparent', border: 0, color: 'var(--pv2-text-subtle)', cursor: 'pointer', marginLeft: 2 }}>
                          <XCircle size={14} strokeWidth={1.5} />
                        </button>
                      )}
                      {(c.status === 'rejected' || c.status === 'suspended') && (
                        <button onClick={() => handleAction(c.id, 'approve')} disabled={actionLoading === c.id} title="Re-approve"
                          style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, background: 'transparent', border: 0, color: 'var(--pv2-green-text)', cursor: 'pointer', marginLeft: 2 }}>
                          <CheckCircle size={14} strokeWidth={1.5} />
                        </button>
                      )}
                    </>
                  )}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Edit modal */}
      {editing && (
        <Modal title="Edit client" onClose={() => setEditing(null)}>
          <div className="pv2-mono-sm" style={{ marginBottom: 16 }}>{editing.email}</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="pv2-field"><label className="pv2-label">First name</label><input className="pv2-input" value={editForm.first_name} onChange={(e) => setEditForm(p => ({ ...p, first_name: e.target.value }))} /></div>
            <div className="pv2-field"><label className="pv2-label">Last name</label><input className="pv2-input" value={editForm.last_name} onChange={(e) => setEditForm(p => ({ ...p, last_name: e.target.value }))} /></div>
          </div>
          <div className="pv2-field"><label className="pv2-label">Firm</label><input className="pv2-input" value={editForm.company_name} onChange={(e) => setEditForm(p => ({ ...p, company_name: e.target.value }))} /></div>
          <div className="pv2-field"><label className="pv2-label">Phone</label><input className="pv2-input" value={editForm.phone} onChange={(e) => setEditForm(p => ({ ...p, phone: formatPhone(e.target.value) }))} placeholder="XXX-XXX-XXXX" inputMode="numeric" data-testid="admin-client-phone" /></div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
            <button onClick={() => setEditing(null)} className="pv2-btn pv2-btn-ghost">Cancel</button>
            <button onClick={saveEdit} disabled={actionLoading === editing.id} className="pv2-btn pv2-btn-primary">{actionLoading === editing.id ? 'Saving…' : 'Save changes'}</button>
          </div>
        </Modal>
      )}

      {/* Delete confirm */}
      {deleteConfirm && (
        <Modal title="Delete client?" onClose={() => setDeleteConfirm(null)}>
          <p style={{ color: 'var(--pv2-text-muted)', fontSize: 14, marginBottom: 16 }}>
            Permanently delete <strong>{deleteConfirm.email}</strong>? This cannot be undone.
          </p>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button onClick={() => setDeleteConfirm(null)} className="pv2-btn pv2-btn-ghost">Cancel</button>
            <button onClick={deleteClient} disabled={actionLoading === deleteConfirm.id} className="pv2-btn pv2-btn-danger">{actionLoading === deleteConfirm.id ? 'Deleting…' : 'Yes, delete'}</button>
          </div>
        </Modal>
      )}

      {/* Email link modal */}
      {linkUser && (
        <Modal title="Link Emails" onClose={() => setLinkUser(null)}>
          <div className="pv2-mono-sm" style={{ marginBottom: 12 }}>{linkUser.first_name} {linkUser.last_name}</div>
          <div style={{ padding: 12, background: 'rgba(96,165,250,0.08)', border: '1px solid rgba(96,165,250,0.25)', fontSize: 12, color: '#93c5fd', marginBottom: 16 }}>
            Link additional email addresses to match jobs from ServeManager that use different emails.
          </div>
          <div className="pv2-mono-sm" style={{ marginBottom: 6 }}>Primary email (login)</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: 10, border: '1px solid var(--pv2-ink-line)', marginBottom: 16 }}><Mail size={12} strokeWidth={1.5} color="var(--pv2-text-muted)" /> {linkUser.email}</div>
          <div className="pv2-mono-sm" style={{ marginBottom: 6 }}>Linked emails ({linkedEmails.length})</div>
          {linkedEmails.length === 0 ? (
            <div style={{ fontSize: 12, fontStyle: 'italic', color: 'var(--pv2-text-subtle)', marginBottom: 16 }}>No linked emails yet</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
              {linkedEmails.map((e, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 10, border: '1px solid var(--pv2-ink-line)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><LinkIcon size={12} strokeWidth={1.5} color="#60a5fa" /> {e}</div>
                  <button onClick={() => removeLinked(e)} disabled={emailLoading} style={{ background: 'transparent', border: 0, color: 'var(--pv2-red-bright)', cursor: 'pointer' }}><Trash2 size={13} /></button>
                </div>
              ))}
            </div>
          )}
          <div className="pv2-mono-sm" style={{ marginBottom: 6 }}>Add email</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input className="pv2-input" type="email" placeholder="email@servemanager.com" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && addLinked()} />
            <button onClick={() => addLinked()} disabled={emailLoading || !newEmail.trim()} className="pv2-btn pv2-btn-primary"><Plus size={13} /> Add</button>
          </div>
          {emailError && <p style={{ marginTop: 8, color: 'var(--pv2-red-bright)', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}><AlertCircle size={12} /> {emailError}</p>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
            <button onClick={() => setLinkUser(null)} className="pv2-btn pv2-btn-ghost">Done</button>
          </div>
        </Modal>
      )}
    </PortalV2Layout>
  );
};

const Modal = ({ children, onClose, title }) => (
  <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }} onClick={onClose}>
    <div style={{ width: '100%', maxWidth: 500, background: 'var(--pv2-ink)', border: '1px solid var(--pv2-ink-line)', padding: 28 }} onClick={(e) => e.stopPropagation()}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h3 className="pv2-display" style={{ fontSize: 22 }}>{title}</h3>
        <button onClick={onClose} style={{ background: 'transparent', border: 0, color: 'var(--pv2-text-muted)', cursor: 'pointer' }}><X size={18} /></button>
      </div>
      {children}
    </div>
  </div>
);

export default AdminV2Clients;
